/**
 * 재시작 원본 실행의 좌표 유도 — **한 규칙**.
 *
 * 산출물(`rstplt`·`baseline.i`)은 `.../simulation/{실행}/{참가자 순번}/` 에
 * 놓인다. 그 순번은 실행 이력이 기록한 참가 모델 목록에서 나온다. 목록에 없는
 * 모델을 0 으로 접으면 **다른 모델의 기준선과 rstplt** 로 재시작이 무음으로
 * 성립한다 — 화면에는 A 라고 적혀 있고 계산은 B 에서 이어진다.
 */
import { runModeLabel, type SimulationEntry } from '@/types/project';
import { runStatusAdjective } from '@/shared/ui/runStatusView';
import { RESTART_STATE_FILE, RUN_BASELINE_DECK_FILE } from '@/app/services/storage/runArtifacts';
import { isCoupling } from '@/domain/cosim/coupling';

/**
 * 이 실행 안에서 그 모델의 참가자 순번. 참가하지 않았거나 **참가 모델이 기록되지 않았으면** `null`.
 * 기록이 없는 실행을 순번 0 으로 보면, 그 실행이 다른 모델의 것이어도(프로젝트에 MARS 모델이 여럿이거나
 * 커플링 실행이면) 그 모델의 원본으로 올라간다 — 누구의 실행인지 모르는 것은 원본이 아니다.
 */
export function runTaskIndexOf(run: SimulationEntry, modelId: string): number | null {
  const index = (run.modelIds ?? []).indexOf(modelId);
  return index >= 0 ? index : null;
}

/**
 * 재시작 원본이 될 수 있는 실행의 상태 — **완료된 실행뿐**이다(2026-09-17 사용자 확정).
 * 재시작은 완료된 실행에서 조건을 바꿔 이어 계산하는 것이지 멈춘 실행의 재개가 아니다. 과도가 끝난 실행만
 * 끝 시각에 마지막 블록을 남기므로 "원본 끝 = 재시작 시각"이 성립한다. Stopped 는 마지막 블록이 멈춘
 * 시점보다 앞이라(어댑터 Stop 은 엔진 종료 루틴을 부르지 않는다) 어디서 이어지는지 사용자가 알 수 없다.
 *
 * 규칙과 화면 문구(`restartSourceEmptyText`)가 이 선언 하나를 쓴다 — 규칙만 바뀌고 문구가 «완료·중지된» 으로
 * 남아 있던 일(2026-10-01 베타 실측)이 다시 없게.
 */
export const RESTART_SOURCE_STATUSES: readonly SimulationEntry['status'][] = ['completed'];
/** 완료된 실행만 남긴다 — 기록 상태는 서버 원문 그대로이고(어휘 밖은 «상태 모름»), 서버가 «완료» 라고 적은 것만 받는다 */
export function restartSourceCandidates(runs: readonly SimulationEntry[]): SimulationEntry[] {
  return runs.filter((e) => RESTART_SOURCE_STATUSES.includes(e.status));
}

/** 원본 실행이 그 참가자 자리에 남겨야 하는 파일 — 재시작 상태와 그 상태를 만든 구성(짝). 하나라도 없으면 원본이 아니다 */
export const RESTART_SOURCE_FILES: readonly string[] = [RESTART_STATE_FILE, RUN_BASELINE_DECK_FILE];

/**
 * 그 모델의 재시작 원본이 될 수 있는 실행 — **목록에는 이것만 올린다**. 셋을 다 채운 실행이다:
 * 완료됐고, 그 모델이 참가했고(순번을 안다), 그 참가자 자리에 재시작 파일과 기준선 덱이 있다.
 * 산출물을 확인하지 못하면 던진다 — 모르는 것을 «없음» 으로 꾸며 목록에서 조용히 빼지 않는다.
 *
 * @param listFileNames 실행과 참가자 순번의 산출물 파일 이름들
 */
export async function restartSources(
  runs: readonly SimulationEntry[],
  modelId: string,
  listFileNames: (run: SimulationEntry, taskIndex: number) => Promise<readonly string[]>,
): Promise<SimulationEntry[]> {
  const mine = runsForModel(restartSourceCandidates(runs), modelId);
  const kept = await Promise.all(mine.map(async (run) => {
    const names = await listFileNames(run, runTaskIndexOf(run, modelId)!);
    return RESTART_SOURCE_FILES.every((file) => names.includes(file)) ? run : null;
  }));
  return kept.filter((run): run is SimulationEntry => run !== null);
}

/** 원본이 하나도 없을 때의 문구 — 받아 주는 상태의 관형형(상태 표현 정본 runStatusAdjective)에서 만든다. 어간을 손으로 잇지 않는다 */
export function restartSourceEmptyText(): string {
  const phrase = RESTART_SOURCE_STATUSES.map(runStatusAdjective).join('·');
  return `재시작 원본이 될 수 있는 ${phrase} 해석이 없습니다. 원본은 이 모델이 참가했고 재시작 파일과 기준선 덱을 남긴 ${phrase} 해석입니다.`;
}

/**
 * 커플링 실행의 참가자인가 — 기록된 실행 모드가 커플링이거나, 모드가 기록되지 않은 구 이력이면 참가 모델이 둘 이상인 것.
 * 단독으로 이어 돌리는 것을 막지는 않는다(디버깅·단독 검토에 쓸모가 있다) — 뜻만 알린다(2026-10-01 사용자 결정).
 */
export function isCoupledRun(run: SimulationEntry): boolean {
  if (run.runMode === 'cosim') return true;
  if (run.runMode === 'single') return false;
  return isCoupling(run.modelIds ?? []);
}

/** 고른 실행에 대해 알릴 것 — 없으면 null */
export function restartSourceNotice(run: SimulationEntry): string | null {
  if (!isCoupledRun(run)) return null;
  return (
    `${runModeLabel('cosim')} 실행의 참가자입니다. 단독으로 이어 돌리면 파트너와 주고받던 경계값은 ` +
    '덱에 적힌 값으로 고정됩니다 — 커플링 실행의 이어 돌리기가 아니라 떼어낸 단독 문제입니다.'
  );
}

export function runsForModel(runs: readonly SimulationEntry[], modelId: string): SimulationEntry[] {
  return runs.filter((r) => runTaskIndexOf(r, modelId) !== null);
}
